
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { fmtUsd, formatCost } from './format-cost';

dayjs.extend(utc);
dayjs.extend(timezone);

export const BILLING_TZ = 'America/New_York';

export function currentBillingMonth(now = dayjs()) {
  return dayjs(now).tz(BILLING_TZ).format('YYYY-MM');
}

export const PACE_ONTRACK_MAX = 1.05;
export const PACE_ATRISK_MAX = 1.25;

export function resolveBillingWindow({ endDate, dataThrough, tokenMaxDate }) {
  const monthEnd = endDate ? dayjs(endDate) : dayjs();
  const monthStart = monthEnd.startOf('month');
  const daysInMonth = monthEnd.daysInMonth();
  const isCurrentMonth = monthEnd.format('YYYY-MM') === currentBillingMonth();
  const watermark = dataThrough || tokenMaxDate;
  const effectiveEnd = watermark && dayjs(watermark).isBefore(monthEnd)
    ? dayjs(watermark)
    : monthEnd;
  const noSettledDay = isCurrentMonth && effectiveEnd.isBefore(monthStart, 'day');
  let cutoffDay = daysInMonth;
  if (isCurrentMonth) cutoffDay = noSettledDay ? 0 : effectiveEnd.date();
  const daysLeft = Math.max(0, daysInMonth - cutoffDay);
  return {
    monthEnd,
    daysInMonth,
    isCurrentMonth,
    effectiveEnd,
    cutoffDay,
    daysLeft,
    cutoffLabel: noSettledDay ? null : effectiveEnd.format('MMM D'),
  };
}

export const TIER_LABEL = {
  code: 'Claude Code',
  cowork: 'Claude Cowork',
  chat: 'Claude Chat',
  unassigned: 'Unassigned',
};

export function paceStatus(pctOfPace) {
  if (!Number.isFinite(pctOfPace)) return null;
  if (pctOfPace <= PACE_ONTRACK_MAX) return 'ontrack';
  if (pctOfPace <= PACE_ATRISK_MAX) return 'atrisk';
  return 'over';
}

export const PACE_LABEL = { ontrack: 'On track', atrisk: 'Running hot', over: 'Over pace' };

export function budgetProjection({ spent, total, cutoffDay, daysInMonth, isCurrentMonth }) {
  if (!isCurrentMonth) return null;
  if (!cutoffDay || spent == null || total == null || total <= 0) return null;
  const rate = spent / cutoffDay;
  const daysRemaining = Math.max(0, daysInMonth - cutoffDay);
  const projectedTotal = spent + rate * daysRemaining;
  const projectedPct = projectedTotal / total;
  return { rate, projectedTotal, projectedPct };
}

export function idealPace({ total, dayOfMonth, daysInMonth }) {
  if (total == null || total <= 0 || !daysInMonth) return null;
  return total * (dayOfMonth / daysInMonth);
}

export function budgetSentence({ total, spent, projection, cutoffDay, daysInMonth }) {
  const fmt = formatCost;
  if (!total || total <= 0) return 'No soft limit set for this period.';
  if (!spent || spent <= 0) {
    return `No spend recorded yet against your ${fmt(total)} soft limit this period.`;
  }
  if (projection) {
    const daysLeft = Math.max(0, daysInMonth - cutoffDay);
    const delta = total - projection.projectedTotal;
    const deltaPhrase = delta >= 0
      ? `${fmt(delta)} under soft limit`
      : `${fmt(Math.abs(delta))} over soft limit`;
    return `You've spent ${fmt(spent)} of your ${fmt(total)} soft limit with ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left. You're trending to ${fmt(projection.projectedTotal)} by month end which is ${deltaPhrase}.`;
  }
  const pct = Math.round((spent / total) * 100);
  const delta = total - spent;
  const deltaPhrase = delta >= 0 ? `${fmt(delta)} under` : `${fmt(Math.abs(delta))} over`;
  return `You spent ${fmt(spent)} of your ${fmt(total)} soft limit this month — ${pct}%, ${deltaPhrase}.`;
}

const round2 = (v) => Math.round(v * 100) / 100;

export function previewIncrease({ entitlement, hardCap, entitlementIncrease, hardCapIncrease }) {
  const newEntitlement = round2(entitlement + (entitlementIncrease || 0));
  const newHardCap = round2(hardCap + (hardCapIncrease || 0));
  const hasIncrease = (entitlementIncrease || 0) > 0 || (hardCapIncrease || 0) > 0;
  const capTooLow = hasIncrease && newHardCap <= newEntitlement;
  return {
    newEntitlement,
    newHardCap,
    hasIncrease,
    capTooLow,
    minHardCapIncrease: capTooLow ? Math.ceil(round2((newEntitlement + 0.01 - hardCap) * 100)) / 100 : 0,
  };
}

export function summarizeTeam(members) {
  const rows = members || [];
  let allocation = 0;
  let spent = 0;
  let hardCap = 0;
  let originalHardCap = 0;
  let withHardCap = 0;
  let extendedCount = 0;
  let withLimit = 0;
  rows.forEach((m) => {
    spent += Number(m.mtd_spend || 0);
    if (m.entitlement != null) {
      allocation += Number(m.entitlement);
      withLimit += 1;
    }
    if (m.hard_cap != null) {
      hardCap += Number(m.hard_cap);
      originalHardCap += Number(m.base_hard_cap ?? m.hard_cap);
      withHardCap += 1;
      if (m.base_hard_cap != null && Number(m.hard_cap) > Number(m.base_hard_cap)) extendedCount += 1;
    }
  });
  return {
    allocation: round2(allocation),
    hardCap: withHardCap > 0 ? round2(hardCap) : null,
    originalHardCap: withHardCap > 0 ? round2(originalHardCap) : null,
    extendedCount,
    spent: round2(spent),
    reportCount: rows.length,
    withoutLimit: rows.length - withLimit,
  };
}

export function teamBudgetSentence({
  total, spent, projection, cutoffDay, daysInMonth, term = 'allocation', subject = 'This team',
}) {
  const named = subject !== 'This team';
  const possessive = named ? `${subject}'s` : "this team's";
  if (!total || total <= 0) return `No ${term} set for this period.`;
  if (!spent || spent <= 0) return `No spend recorded yet against ${possessive} ${fmtUsd(total)} ${term} this period.`;
  if (projection) {
    const daysLeft = Math.max(0, daysInMonth - cutoffDay);
    const delta = total - projection.projectedTotal;
    const deltaPhrase = delta >= 0 ? `${fmtUsd(delta)} under ${term}` : `${fmtUsd(Math.abs(delta))} over ${term}`;
    return `${subject} has spent ${fmtUsd(spent)} of its ${fmtUsd(total)} ${term} with ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left. It's trending to ${fmtUsd(projection.projectedTotal)} by month end which is ${deltaPhrase}.`;
  }
  const delta = total - spent;
  const deltaPhrase = delta >= 0 ? `${fmtUsd(delta)} under` : `${fmtUsd(Math.abs(delta))} over`;
  return `${subject} spent ${fmtUsd(spent)} of its ${fmtUsd(total)} ${term} this month — ${Math.round((spent / total) * 100)}%, ${deltaPhrase}.`;
}

export function spendRisk({ spend, projected, softLimit, hardCap, originalCap }) {
  const cap = hardCap || 0;
  const original = originalCap || 0;
  const soft = softLimit || 0;
  if (cap > original && projected > cap) return 'atrisk';
  if (original > 0 && spend >= original) return 'overcap';
  if (cap > 0 && projected > cap) return 'atrisk';
  if (soft > 0 && projected > soft) return 'oversoft';
  return 'ontrack';
}

export const SPEND_RISK_ORDER = { overcap: 0, atrisk: 1, oversoft: 2, ontrack: 3 };

export const HARD_CAP_HEADROOM = 0.15;

export function suggestedHardCap({ projected, originalCap }) {
  return Math.max(originalCap || 0, Math.ceil((projected * (1 + HARD_CAP_HEADROOM)) / 50) * 50);
}

export function buildSpendRows(members, { cutoffDay, daysInMonth, isCurrentMonth }) {
  const branchSize = {};
  (members || []).forEach((m) => { branchSize[m.branch_email] = (branchSize[m.branch_email] || 0) + 1; });
  const rows = (members || []).map((m) => {
    const spend = Number(m.mtd_spend || 0);
    const softLimit = m.entitlement ?? null;
    const hardCap = m.hard_cap ?? null;
    const originalCap = m.base_hard_cap ?? hardCap;
    const proj = budgetProjection({ spent: spend, total: softLimit, cutoffDay, daysInMonth, isCurrentMonth });
    const projected = proj ? proj.projectedTotal : spend;
    return {
      ...m,
      spend,
      softLimit,
      hardCap,
      originalCap,
      minCap: m.min_hard_cap ?? originalCap,
      projected,
      extended: hardCap != null && originalCap != null && hardCap > originalCap,
      headcount: m.depth === 1 ? branchSize[m.email] || 1 : null,
      pctUsed: softLimit > 0 ? spend / softLimit : 0,
      risk: spendRisk({ spend, projected, softLimit, hardCap, originalCap }),
    };
  });
  const capShare = (r) => (r.hardCap > 0 ? r.projected / r.hardCap : 0);
  return rows.sort((a, b) => (SPEND_RISK_ORDER[a.risk] - SPEND_RISK_ORDER[b.risk]) || (capShare(b) - capShare(a)));
}

export function chainToPerson(rows, viewerEmail, email) {
  const byEmail = new Map((rows || []).map((r) => [r.email, r]));
  const chain = [];
  const seen = new Set();
  let cur = byEmail.get(email);
  while (cur && cur.email !== viewerEmail && !seen.has(cur.email)) {
    seen.add(cur.email);
    chain.unshift(cur);
    cur = byEmail.get(cur.manager_email);
  }
  return chain;
}

export function allocationStatus(pctOfAllocation) {
  if (pctOfAllocation == null) return null;
  return paceStatus(Number(pctOfAllocation) / 100);
}

export function computeTeamKpis({ allocation, spent, cutoffDay, daysInMonth, isCurrentMonth }) {
  const dayOfMonth = isCurrentMonth ? cutoffDay : daysInMonth;
  const ideal = idealPace({ total: allocation, dayOfMonth, daysInMonth });
  const havePace = ideal != null && ideal > 0;
  const pctOfPace = havePace ? spent / ideal : null;
  return {
    remaining: (allocation || 0) - (spent || 0),
    pctOfAllocation: allocation > 0 ? Math.round((spent / allocation) * 100) : null,
    pctOfPace,
    paceKind: havePace ? paceStatus(pctOfPace) : null,
    projection: budgetProjection({ spent, total: allocation, cutoffDay, daysInMonth, isCurrentMonth }),
  };
}
